/* External Deps */
import { FC, ReactElement, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Close, Download, Visibility, Edit } from "@mui/icons-material";

/* Internal Deps */
import { IconImage } from "@/components";
import { useGetProductCertQuery } from "@/services";
import { formatDateOrTime } from "@/utils";
import { excel_img, report_cross } from "@/constants";
import { useCertificateGenerator } from "../certificate-generator";
import { useAppSelector } from "@/store";
import { DocumentAccessLevel, roles } from "@/roles";
import { renderExtractedData } from "./renderExtractedData";

interface CertificateDetailModalProps {
  open: boolean;
  onClose: () => void;
  certificateId: string | null;
  onEdit?: () => void;
}

const CertificateDetailModal: FC<CertificateDetailModalProps> = ({
  open,
  onClose,
  certificateId,
  onEdit,
}): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const {
    data: certificate,
    isLoading,
    error,
  } = useGetProductCertQuery(
    { id: certificateId! },
    { skip: !certificateId || !open }
  );

  const { generatePDF, previewCertificate } = useCertificateGenerator();

  const canEdit = useMemo(
    () =>
      user?.userRole !== roles.SPCLIENT ||
      user?.clientPermission?.documents === DocumentAccessLevel.Admin,
    [user]
  );

  const handleDownload = async () => {
    if (!certificate) return;
    
    try {
      await generatePDF(certificate);
    } catch (error) {
      console.error("Error generating certificate:", error);
    }
  };

  const handlePreview = async () => {
    if (!certificate) return;
    
    try {
      await previewCertificate(certificate);
    } catch (error) {
      console.error("Error previewing certificate:", error);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconImage src={excel_img} size="32px" />
          <Box>
            <Typography variant="h6">
              {certificate?.fileName || "Certificate Details"}
            </Typography>
            {certificate && (
              <Typography variant="body2" color="text.secondary">
                Uploaded on {formatDateOrTime(certificate.uploadDate, "date")} at {formatDateOrTime(certificate.uploadDate, "time")}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton 
            onClick={handlePreview} 
            size="small"
            title="Preview Certificate"
            sx={{ color: "primary.main" }}
          >
            <Visibility />
          </IconButton>
          <IconButton 
            onClick={handleDownload} 
            size="small"
            title="Download Certificate PDF"
            sx={{ color: "success.main" }}
          >
            <Download />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load certificate details. Please try again.
          </Alert>
        )}

        {certificate && (
          <Box>
            {/* Certificate Metadata */}
            <Card sx={{ mb: 3, bgcolor: "grey.50" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconImage src={report_cross} size="24px" />
                  Certificate Information
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>File Name:</Typography>
                  <Typography variant="body2">{certificate.fileName}</Typography>
                  
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Item Count:</Typography>
                  <Typography variant="body2">{certificate.itemCount} items</Typography>
                  
                  {certificate.companyName && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Company:</Typography>
                      <Typography variant="body2">{certificate.companyName}</Typography>
                    </>
                  )}
                  
                  
                  {certificate.formType && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Form Type:</Typography>
                      <Chip
                        label={certificate.formType}
                        size="small"
                        color={certificate.formType === "USMCA" ? "primary" : "default"}
                        variant="outlined"
                      />
                    </>
                  )}
                  
                </Box>
              </CardContent>
            </Card>

            {/* Extracted Data */}
            {renderExtractedData(certificate)}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {canEdit && onEdit && (
          <Button
            onClick={onEdit}
            variant="contained"
            startIcon={<Edit />}
            color="primary"
          >
            Edit Certificate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CertificateDetailModal;